import React from 'react';
import { AlertTriangle, Banknote, CreditCard, Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatMontant } from './etatSalairesUtils';

// Cartes de statistiques calquées sur MaCaisse : fond pastel + border-2,
// pastille d'icône, puis encadré blanc interne avec lignes et ligne de total.
const EtatSalairesStatsCards = ({ resume = {}, monthLabel, isLoading }) => {
  const effectifUnique = resume.effectif_unique || resume.effectif || 0;
  const nbLignes = resume.effectif || 0;
  const brut = Number(resume.salaire_brut_total || 0);
  const retenuesTotal = Number(resume.retenues_total || 0);
  const retenuesNonImputees = Number(resume.retenues_non_imputees_total || 0);
  const retenuesHorsAvances = Number(
    resume.retenues_hors_avances_total
    || (Number(resume.retenues_presence_total || 0) + Number(resume.retenues_enfants_total || 0))
    || Math.max(0, retenuesTotal - Number(resume.avances_total || 0))
  );
  const avances = Number(resume.avances_total || 0);
  const retenuesImputees = Number(resume.retenues_imputees_total || Math.max(0, retenuesTotal - retenuesNonImputees));
  const netTotal = Number(resume.net_a_payer_total || 0);
  const dejaPaye = Number(resume.deja_paye_total || 0);
  const resteADoter = Number(resume.reste_a_doter || 0);
  const lignesEnAlerte = Number(resume.alertes || 0);
  const lignesPointagesManquants = Number(resume.lignes_pointages_manquants || 0);
  const pointagesManquantsTotal = Number(resume.pointages_manquants_total || 0);
  const controlesOk = lignesEnAlerte === 0 && pointagesManquantsTotal === 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 px-6">
      {/* Montant à doter */}
      <Card className="bg-blue-50 border-2 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center">
              <Banknote size={22} className="text-blue-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Montant à doter</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Net à payer du mois</span>
              <span className="font-semibold text-gray-700 text-right">{formatMontant(netTotal)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Déjà payé</span>
              <span className="font-semibold text-green-600 text-right">
                {dejaPaye > 0 ? `-${formatMontant(dejaPaye)}` : formatMontant(0)}
              </span>
            </div>
            <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Reste à doter</span>
              <span className="text-xl font-bold text-[#246990] text-right">{formatMontant(resteADoter)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Effectif de paie */}
      <Card className="bg-green-50 border-2 border-green-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center">
              <Users size={22} className="text-green-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Effectif de paie</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Employés uniques</span>
              <span className="font-semibold text-gray-700 text-right">{effectifUnique}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Composantes de paie</span>
              <span className="font-semibold text-gray-700 text-right">{nbLignes}</span>
            </div>
            <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total brut</span>
              <span className="text-xl font-bold text-green-600 text-right">{formatMontant(brut)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Déductions */}
      <Card className="bg-orange-50 border-2 border-orange-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center">
              <CreditCard size={22} className="text-orange-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Déductions</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Retenues hors avances</span>
              <span className="font-semibold text-red-600 text-right">{formatMontant(retenuesHorsAvances)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Avances déductibles</span>
              <span className="font-semibold text-amber-600 text-right">{formatMontant(avances)}</span>
            </div>
            {retenuesNonImputees > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
                <div className="flex justify-between items-center gap-3 text-sm">
                  <span className="text-amber-800">Non imputées</span>
                  <span className="font-semibold text-amber-700 text-right">{formatMontant(retenuesNonImputees)}</span>
                </div>
                <p className="mt-1 text-[11px] leading-4 text-amber-700">Montant qui dépasse le brut disponible d'une ligne.</p>
              </div>
            )}
            <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Déductions imputées</span>
              <span className="text-xl font-bold text-orange-600 text-right">{formatMontant(retenuesImputees)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contrôles */}
      <Card className="bg-purple-50 border-2 border-purple-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={22} className={controlesOk ? 'text-green-600' : 'text-purple-600'} />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Contrôles</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Lignes en alerte</span>
              <span className={lignesEnAlerte > 0 ? 'font-semibold text-orange-600 text-right' : 'font-semibold text-gray-700 text-right'}>
                {lignesEnAlerte}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Pointages à régulariser</span>
              <span className={lignesPointagesManquants > 0 ? 'font-semibold text-orange-600 text-right' : 'font-semibold text-gray-700 text-right'}>
                {lignesPointagesManquants}
              </span>
            </div>
            <div className="border-t border-purple-200 pt-2 mt-2">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Jours/créneaux manquants</span>
                <span className={controlesOk ? 'text-xl font-bold text-green-600 text-right' : 'text-xl font-bold text-purple-700 text-right'}>
                  {pointagesManquantsTotal}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">{isLoading ? "Lecture de l'état en cours…" : `Mois lu : ${monthLabel}`}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EtatSalairesStatsCards;
